// src/cockpit4/components/PersonalVoiceManager.tsx
// ============================================
// 口吻管理面板（v6.3.16）—— 内容工厂内嵌
//
// 功能：用户自助管理"个人语料库"（口吻注入的唯一真相源）
//   1. 查看语料统计：问答条数 / 文章篇数 / 总字数
//   2. 新增问答：问 + 答 → 追加到《个人语料-知乎问答.md》（vault-api 写回）
//   3. 新增文章：标题 + 正文 → 追加到《个人语料-文章.md》
//   4. 预览最新语料开头，确认注入已生效
//
// 原则：读写经 Vault（Obsidian 唯一真相源，架构铁律）
// ============================================

import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  MessageSquareText,
  FileText,
  Plus,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertCircle,
  BookOpenText,
} from "lucide-react";

const QA_PATH = "XEAOS/PersonalVoice/个人语料-知乎问答.md";
const ARTICLE_PATH = "XEAOS/PersonalVoice/个人语料-文章.md";

// ============================================
// 主组件
// ============================================

export default function PersonalVoiceManager() {
  const [qaCount, setQaCount] = useState(0);
  const [articleCount, setArticleCount] = useState(0);
  const [qaChars, setQaChars] = useState(0);
  const [articleChars, setArticleChars] = useState(0);
  const [qaPreview, setQaPreview] = useState("");
  const [articlePreview, setArticlePreview] = useState("");

  // 新增问答表单
  const [qaQuestion, setQaQuestion] = useState("");
  const [qaAnswer, setQaAnswer] = useState("");
  // 新增文章表单
  const [articleTitle, setArticleTitle] = useState("");
  const [articleBody, setArticleBody] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const read = async (path: string) => {
        const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(path)}`).catch(() => null);
        if (!resp || !resp.ok) return "";
        const result = await resp.json();
        return result.success ? String(result.content || "") : "";
      };
      const [qa, article] = await Promise.all([read(QA_PATH), read(ARTICLE_PATH)]);
      const qaClean = qa.replace(/^---\n[\s\S]*?\n---\n/, "").trim();
      const articleClean = article.replace(/^---\n[\s\S]*?\n---\n/, "").trim();
      setQaCount((qaClean.match(/^## .*问答/gm) || []).length);
      setArticleCount((articleClean.match(/^## 文章\d+/gm) || []).length);
      setQaChars(qaClean.length);
      setArticleChars(articleClean.length);
      // 预览（取最近一段的 120 字）
      const qaSections = qaClean.split(/^## /gm).filter(Boolean);
      setQaPreview(qaSections.length > 0 ? qaSections[qaSections.length - 1].trim().slice(0, 160) : "");
      const artSections = articleClean.split(/^## /gm).filter(Boolean);
      setArticlePreview(artSections.length > 0 ? artSections[artSections.length - 1].trim().slice(0, 160) : "");
    } catch (err) {
      setMessage({ type: "err", text: `语料加载失败：${(err as Error).message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  // 追加内容到 Vault 文件
  const appendToVault = async (path: string, appendText: string) => {
    const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(path)}`).catch(() => null);
    const current = resp && resp.ok ? await resp.json() : null;
    const currentContent = current?.success ? String(current.content || "") : "";
    const newContent = currentContent.trimEnd() + "\n\n" + appendText.trim() + "\n";
    const writeResp = await fetch(`/vault-api/write?path=${encodeURIComponent(path)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: newContent }),
    });
    const writeResult = await writeResp.json();
    if (!writeResult.success) throw new Error(writeResult.error || "写入失败");
  };

  // 序号工具：下一个中文序号（二十二 → 二十三）
  const nextChineseNum = (count: number): string => {
    const nums = ["一","二","三","四","五","六","七","八","九","十","十一","十二","十三","十四","十五","十六","十七","十八","十九","二十","二十一","二十二","二十三","二十四","二十五","二十六","二十七","二十八","二十九","三十"];
    return nums[count] || String(count + 1);
  };

  const handleAddQa = async () => {
    const q = qaQuestion.trim();
    const a = qaAnswer.trim();
    if (!q || !a) {
      setMessage({ type: "err", text: "问题和回答都要填写" });
      return;
    }
    // 合规过滤
    if (/量化|外汇|股票|期货/.test(a)) {
      setMessage({ type: "err", text: "语料中不能包含金融/量化内容（合规红线，不进公开语料）" });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const idx = nextChineseNum(qaCount); // 当前条数作为下一序号
      const num = qaCount + 1;
      const entry =
        `## ${idx}、问答 ${num}（${q.slice(0, 18)}）\n\n` +
        `**问：** ${q}\n\n` +
        `**答：** ${a}\n`;
      await appendToVault(QA_PATH, entry);
      setQaQuestion("");
      setQaAnswer("");
      setMessage({ type: "ok", text: `✅ 问答 ${num} 已注入语料库（已同步 Obsidian）` });
      await loadStats();
    } catch (err) {
      setMessage({ type: "err", text: `注入失败：${(err as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleAddArticle = async () => {
    const t = articleTitle.trim();
    const b = articleBody.trim();
    if (!t || !b) {
      setMessage({ type: "err", text: "标题和正文都要填写" });
      return;
    }
    if (/量化|外汇|股票|期货/.test(b)) {
      setMessage({ type: "err", text: "语料中不能包含金融/量化内容（合规红线，不进公开语料）" });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const num = articleCount + 1;
      const entry =
        `## 文章${num}：${t}（作者亲笔，手动注入）\n\n` +
        `${b}\n`;
      await appendToVault(ARTICLE_PATH, entry);
      setArticleTitle("");
      setArticleBody("");
      setMessage({ type: "ok", text: `✅ 文章${num} 已注入语料库（已同步 Obsidian）` });
      await loadStats();
    } catch (err) {
      setMessage({ type: "err", text: `注入失败：${(err as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />

      {/* 头部 */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-purple-500/15 border border-purple-500/30">
            <BookOpenText size={13} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">口吻管理（语料注入）</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Personal Voice · 内容生成的"人格层"· 读写 Obsidian
            </p>
          </div>
        </div>
        <button
          onClick={() => void loadStats()}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors"
        >
          <RefreshCw size={11} className="text-purple-400" />
          <span className="text-[9px] font-mono text-purple-300 uppercase">刷新</span>
        </button>
      </div>

      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className={`flex items-start gap-2 px-3 py-2 rounded-md border text-[10px] font-mono ${message.type === "ok" ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300" : "border-red-500/30 bg-red-500/[0.06] text-red-300"}`}>
            {message.type === "ok" ? <CheckCircle2 size={11} className="flex-shrink-0 mt-px" /> : <AlertCircle size={11} className="flex-shrink-0 mt-px" />}
            {message.text}
          </div>
        </div>
      )}

      <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-260px)]">
        {/* 语料统计 */}
        {loading ? (
          <div className="flex items-center gap-2 py-6 justify-center text-gray-500">
            <Loader2 size={14} className="animate-spin" />
            <span className="text-[11px] font-mono">加载语料统计…</span>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
              <div className="text-[18px] font-bold text-purple-300">{qaCount}</div>
              <div className="text-[9px] font-mono text-gray-500 mt-0.5">问答样本</div>
            </div>
            <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
              <div className="text-[18px] font-bold text-emerald-300">{articleCount}</div>
              <div className="text-[9px] font-mono text-gray-500 mt-0.5">文章样本</div>
            </div>
            <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
              <div className="text-[18px] font-bold text-cyan-300">{Math.round(qaChars / 1000)}k</div>
              <div className="text-[9px] font-mono text-gray-500 mt-0.5">问答字数</div>
            </div>
            <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
              <div className="text-[18px] font-bold text-amber-300">{Math.round(articleChars / 1000)}k</div>
              <div className="text-[9px] font-mono text-gray-500 mt-0.5">文章字数</div>
            </div>
          </div>
        )}

        {/* 新增问答 */}
        <div className="rounded-lg border border-purple-500/15 bg-purple-500/[0.03] p-3">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquareText size={11} className="text-purple-400" />
            <span className="text-[11px] font-medium text-gray-300">新增问答样本</span>
            <span className="text-[9px] font-mono text-gray-600">追加到《个人语料-知乎问答.md》</span>
          </div>
          <div className="space-y-2">
            <input
              value={qaQuestion}
              onChange={(e) => setQaQuestion(e.target.value)}
              placeholder="问题（知乎上的提问，例如：普通人怎么开始学AI？）"
              className="w-full px-3 py-2 rounded-md bg-[#020817]/80 border border-white/[0.08] text-[12px] text-gray-200 outline-none focus:border-purple-500/40"
            />
            <textarea
              value={qaAnswer}
              onChange={(e) => setQaAnswer(e.target.value)}
              rows={4}
              placeholder="你的回答（亲笔写，越像你说话越好：判断开场、大白话、亲身经历、本质总结）"
              className="w-full px-3 py-2 rounded-md bg-[#020817]/80 border border-white/[0.08] text-[12px] text-gray-200 outline-none focus:border-purple-500/40 resize-y"
            />
            <button
              onClick={handleAddQa}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-500/15 border border-purple-500/30 text-purple-300 hover:bg-purple-500/25 transition-colors disabled:opacity-50 text-[10px] font-mono"
            >
              {saving ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />}
              注入问答
            </button>
          </div>
        </div>

        {/* 新增文章 */}
        <div className="rounded-lg border border-emerald-500/15 bg-emerald-500/[0.03] p-3">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={11} className="text-emerald-400" />
            <span className="text-[11px] font-medium text-gray-300">新增文章样本</span>
            <span className="text-[9px] font-mono text-gray-600">追加到《个人语料-文章.md》· 长文口吻</span>
          </div>
          <div className="space-y-2">
            <input
              value={articleTitle}
              onChange={(e) => setArticleTitle(e.target.value)}
              placeholder="文章标题（例如：我是怎么All in AI的）"
              className="w-full px-3 py-2 rounded-md bg-[#020817]/80 border border-white/[0.08] text-[12px] text-gray-200 outline-none focus:border-emerald-500/40"
            />
            <textarea
              value={articleBody}
              onChange={(e) => setArticleBody(e.target.value)}
              rows={5}
              placeholder="文章正文（长文结构样本：开头判断/叙事节奏/分点展开/本质总结）"
              className="w-full px-3 py-2 rounded-md bg-[#020817]/80 border border-white/[0.08] text-[12px] text-gray-200 outline-none focus:border-emerald-500/40 resize-y"
            />
            <button
              onClick={handleAddArticle}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors disabled:opacity-50 text-[10px] font-mono"
            >
              {saving ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />}
              注入文章
            </button>
          </div>
        </div>

        {/* 最近注入预览 */}
        <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3 space-y-3">
          <div className="text-[10px] font-mono text-gray-400">最近语料预览（确认注入生效）</div>
          {qaPreview && (
            <div>
              <div className="text-[9px] font-mono text-purple-400 mb-1">📝 最新问答</div>
              <div className="text-[11px] text-gray-400 leading-relaxed line-clamp-3">{qaPreview}</div>
            </div>
          )}
          {articlePreview && (
            <div>
              <div className="text-[9px] font-mono text-emerald-400 mb-1">📄 最新文章</div>
              <div className="text-[11px] text-gray-400 leading-relaxed line-clamp-3">{articlePreview}</div>
            </div>
          )}
        </div>

        {/* 提示 */}
        <div className="rounded-lg border border-cyan-500/15 bg-cyan-500/[0.03] p-3">
          <div className="text-[10px] text-cyan-300/80 leading-relaxed">
            口吻注入 = 内容生成的"人格层"。注入的问答/文章会立即进入全链路（每周大纲 → 详细大纲 → 全平台品牌脚本）自动生效，无需重启。
            合规提醒：金融/量化/外汇内容不能进语料（公开内容红线）；不放微信/电话。
          </div>
        </div>
      </div>
    </motion.div>
  );
}
